import React from "react";
import { Link, useRoute } from "wouter";

const logoHeader = '/assets/ALWAYS_logo_Web_RGB_POS.png';
const logoFooter = '/assets/ALWAYS_logo_Web_RGB_NEG.png';

const legalContent: Record<string, { title: string; lastUpdated: string; content: JSX.Element }> = {
  'privacy-policy': {
    title: 'Privacy Policy',
    lastUpdated: 'September 5, 2025',
    content: (
      <>
        <p className="mb-6">
          Allwayswithyou Limited ("we", "our", "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal data when you use our Care Plan Manager (CPM) application and website.
        </p>
        <p className="mb-6 font-semibold">
          Important: By using our services, you agree to the practices described in this Privacy Policy.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">1. Who We Are</h3>
        <p className="mb-2"><strong>Allwayswithyou Limited</strong></p>
        <p className="mb-2">Sonas, Quin, Co. Clare, V95 Y031, Ireland</p>
        <p className="mb-2">Company Registration Number: 622071</p>
        <p className="mb-2">VAT Number: IE3530481GH</p>
        <p className="mb-6">Email: <a href="mailto:support@allwayswithyou.com" className="text-blue-600 hover:underline">support@allwayswithyou.com</a></p>
        <p className="mb-6">We are the Data Controller responsible for processing your personal data.</p>

        <h3 className="text-2xl font-bold mt-8 mb-4">2. What Data We Collect</h3>
        <p className="mb-4">We collect the following categories of personal data through the CPM platform:</p>
        
        <h4 className="text-xl font-semibold mt-6 mb-3">Client/Service User Data</h4>
        <p className="mb-6">Name, date of birth, contact details, comprehensive medical history, health conditions, care assessments, treatment plans, medication information, and related health and wellbeing data provided by assessors or care coordinators.</p>

        <h4 className="text-xl font-semibold mt-6 mb-3">Assessor & Care Coordinator Data</h4>
        <p className="mb-6">Name, role, organisation, work email, digital signatures, and notes recorded during assessments and care planning.</p>

        <h4 className="text-xl font-semibold mt-6 mb-3">Nominated Person Data</h4>
        <p className="mb-6">Name, relationship to the client, and contact information, where provided as part of the care plan.</p>

        <h4 className="text-xl font-semibold mt-6 mb-3">Account Data</h4>
        <p className="mb-6">Login credentials (email, password), and session details such as authentication tokens.</p>

        <h4 className="text-xl font-semibold mt-6 mb-3">Support Communications</h4>
        <p className="mb-6">Records of queries, feedback, or issues raised with our support team.</p>

        <h3 className="text-2xl font-bold mt-8 mb-4">3. Purpose & Legal Basis for Processing</h3>
        <p className="mb-4">We process personal data for the following purposes:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li><strong>Provision of Services:</strong> to create, update, and manage digital care plans, assessments, and related records (basis: contract).</li>
          <li><strong>Compliance & Record-Keeping:</strong> to help care providers meet regulatory obligations (basis: legal obligation).</li>
          <li><strong>User Management:</strong> to manage assessor, coordinator, and administrator accounts and permissions (basis: legitimate interest).</li>
          <li><strong>Client & Nominated Person Communication:</strong> to support secure communication and digital sign-off (basis: consent/legitimate interest).</li>
          <li><strong>Support & Troubleshooting:</strong> to respond to issues, ensure platform performance, and improve services (basis: legitimate interest).</li>
        </ul>
        <p className="mb-6 font-semibold">We do not use CPM data for advertising, marketing, or profiling.</p>

        <h3 className="text-2xl font-bold mt-8 mb-4">4. Cookies</h3>
        <p className="mb-6">
          We only use <strong>strictly necessary cookies</strong> that are essential for the Care Plan Manager to function (such as login session cookies and authentication tokens).
        </p>
        <p className="mb-6">
          We do not use analytics, marketing, or tracking cookies. Consent is not required for these essential cookies under GDPR.
        </p>
        <p className="mb-6">
          Full details are provided in our <Link href="/cookie-policy" className="text-blue-600 hover:underline">Cookie Policy</Link>.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">5. Where Data Is Processed</h3>
        <p className="mb-6">
          Personal data is stored on secure cloud infrastructure utilizing enterprise-grade PostgreSQL databases. The hosting infrastructure is located in data centers in the United States.
        </p>
        <p className="mb-4">As data is processed outside the European Economic Area (EEA), we rely on appropriate safeguards including:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Industry-standard compliance certifications (ISO 27001, SOC 2 Type 2)</li>
          <li>Comprehensive security frameworks and attestations</li>
          <li>Standard contractual clauses and adequate security measures for international transfers</li>
          <li>Continuous monitoring and vulnerability assessments</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">6. Data Retention</h3>
        <p className="mb-4">We retain personal data for as long as your account is active or as required to provide services:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Closed account data is deleted within 30 days.</li>
          <li>Backups and logs are retained for up to 6 months.</li>
          <li>Some data may be retained longer if required by law.</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">7. Sharing of Data</h3>
        <p className="mb-4">We do not sell or rent personal data. Data is only shared with:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Service providers (e.g. hosting partners, Stripe for payments) under data processing agreements.</li>
          <li>Regulatory or legal authorities, if required by law.</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">8. Security</h3>
        <p className="mb-4">We use technical and organisational measures to protect your personal data, including:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>HTTPS encryption</li>
          <li>Access controls</li>
          <li>Short-lived session cookies with automatic expiry</li>
          <li>Continuous monitoring for vulnerabilities</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">9. Children's Privacy</h3>
        <p className="mb-6">
          The CPM service is intended for professional care providers and adult clients. We do not knowingly collect personal data from children under 18.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">10. Your Rights</h3>
        <p className="mb-4">Under GDPR, you have the following rights:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Right to be informed</li>
          <li>Right of access</li>
          <li>Right to rectification</li>
          <li>Right to erasure</li>
          <li>Right to restrict processing</li>
          <li>Right to data portability</li>
          <li>Right to object</li>
          <li>Rights regarding automated decision-making and profiling</li>
        </ul>
        <p className="mb-4"><strong>To exercise these rights:</strong> Contact us at <a href="mailto:support@allwayswithyou.com" className="text-blue-600 hover:underline">support@allwayswithyou.com</a></p>
        <p className="mb-6">
          <strong>You may also lodge a complaint</strong> with the Data Protection Commission (DPC): <a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.dataprotection.ie</a>
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">11. Updates to This Policy</h3>
        <p className="mb-6">
          We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">12. Contact</h3>
        <p className="mb-2"><strong>For questions about this Privacy Policy, contact:</strong></p>
        <p className="mb-6"><a href="mailto:support@allwayswithyou.com" className="text-blue-600 hover:underline">support@allwayswithyou.com</a></p>
      </>
    )
  },
  'terms-conditions': {
    title: 'Terms & Conditions',
    lastUpdated: 'September 5, 2025',
    content: (
      <>
        <p className="mb-6">
          These Website Terms of Use ("Terms") govern your access to and use of the website operated by Allwayswithyou Limited ("we", "us", "our"). By accessing or using this website, you agree to be bound by these Terms. If you do not agree, you must not use this website.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">1. Intellectual Property</h3>
        <p className="mb-6">
          This website and its content are owned and operated by Allwayswithyou Limited. Unless otherwise stated, all text, images, graphics, software, trademarks, logos, and other material appearing on this website are our intellectual property or licensed to us.
        </p>
        <p className="mb-6">
          You may not reproduce, copy, distribute, modify, display, publish, or transmit any part of this website without our prior written consent, except for personal, non-commercial use.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">2. Indemnification</h3>
        <p className="mb-6">
          If you reproduce, display, transmit, distribute, or otherwise exploit any content from this website in a manner not authorised by us, or if you otherwise infringe our intellectual property rights or breach these Terms, you agree to indemnify and hold Allwayswithyou Limited harmless from and against any claims, damages, costs, expenses, or liabilities (including reasonable legal fees) arising out of such actions.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">3. Privacy & Data Protection</h3>
        <p className="mb-6">
          Your privacy is important to us. We process personal data in accordance with our Privacy Policy, which explains what data we collect, how we use it, and your rights under the General Data Protection Regulation (GDPR).
        </p>
        <p className="mb-6">
          By using this website or contacting us electronically, you consent to receiving communications from us electronically. Such communications satisfy any legal requirement that they be in writing.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">4. Security</h3>
        <p className="mb-6">
          We take reasonable technical and organisational measures to protect the integrity and security of this website. However, we cannot guarantee that the site will be uninterrupted, error-free, or free from malicious code. You are responsible for implementing suitable safeguards on your own systems.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">5. External Links</h3>
        <p className="mb-6">
          This website may contain links to third-party websites. We have no control over the content, policies, or practices of such websites and accept no responsibility for them. Accessing third-party websites is at your own risk.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">6. Social Media</h3>
        <p className="mb-6">
          Your engagement with us on external social media platforms is subject to the terms and privacy policies of those platforms. We will never request sensitive personal information through social media. If you wish to share such information, please use direct contact channels such as email or telephone.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">7. Automated Access</h3>
        <p className="mb-6">
          The use of automated tools such as bots, scrapers, or web crawlers to access or collect data from this website is prohibited if such activity may impair the site's performance, compromise its security, or infringe our rights.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">8. Prohibited Use</h3>
        <p className="mb-4">You may not:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Attempt to gain unauthorised access to this website or related systems;</li>
          <li>Introduce viruses, malware, or harmful code;</li>
          <li>Use this website in a way that could damage, disable, or impair it;</li>
          <li>Use the website for unlawful, fraudulent, or harmful purposes.</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">9. Governing Law & Jurisdiction</h3>
        <p className="mb-6">
          These Terms are governed by the laws of Ireland. Any disputes arising in connection with these Terms or your use of this website shall be subject to the exclusive jurisdiction of the Irish courts.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">10. Changes to These Terms</h3>
        <p className="mb-6">
          We may revise these Terms at any time by posting an updated version on this website. Continued use of the website after changes take effect constitutes acceptance of the revised Terms.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">11. Contact Information</h3>
        <p className="mb-2"><strong>Allwayswithyou Limited</strong></p>
        <p className="mb-2">Sonas, Quin, Co Clare, V95Y031, Ireland</p>
        <p className="mb-2">Company Registration No: 622071</p>
        <p className="mb-2">VAT No: IE3530481GH</p>
        <p className="mb-6">Email: <a href="mailto:support@allwayswithyou.com" className="text-blue-600 hover:underline">support@allwayswithyou.com</a></p>
      </>
    )
  },
  'cookie-policy': {
    title: 'Cookie Policy',
    lastUpdated: 'September 5, 2025',
    content: (
      <>
        <p className="mb-6">
          This Cookie Policy explains how Allwayswithyou Limited uses cookies and similar technologies when you visit our Care Plan Manager application. We are committed to protecting your privacy and only use cookies that are essential for the proper functioning of our service.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">What Are Cookies?</h3>
        <p className="mb-6">
          Cookies are small text files that are stored on your device when you visit a website. They help websites remember information about your visit, such as your login status and preferences, which can make your next visit easier and the site more useful to you.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">Cookies We Use</h3>
        <p className="mb-6">
          We only use <strong>strictly necessary cookies</strong> that are essential for the operation of our Care Plan Manager. We do not use any tracking, analytics, advertising, or marketing cookies.
        </p>
        
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">Cookie Name</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Purpose</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Duration</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Type</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2"><code>session</code></td>
                <td className="border border-gray-300 px-4 py-2">Maintains your login session and user authentication</td>
                <td className="border border-gray-300 px-4 py-2">1 hour or until logout</td>
                <td className="border border-gray-300 px-4 py-2">Essential</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2"><code>firebase_user</code></td>
                <td className="border border-gray-300 px-4 py-2">Firebase authentication token for secure login</td>
                <td className="border border-gray-300 px-4 py-2">As configured by Firebase</td>
                <td className="border border-gray-300 px-4 py-2">Essential</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2"><code>remember_token</code></td>
                <td className="border border-gray-300 px-4 py-2">Remembers your login preference (if selected)</td>
                <td className="border border-gray-300 px-4 py-2">Until manually cleared</td>
                <td className="border border-gray-300 px-4 py-2">Essential</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-2xl font-bold mt-8 mb-4">Why We Use These Cookies</h3>
        <p className="mb-4">All cookies used by our Care Plan Manager are <strong>strictly necessary</strong> for:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Maintaining your secure login session</li>
          <li>Remembering your authentication status</li>
          <li>Preserving form data during multi-step assessments</li>
          <li>Ensuring proper security and access control</li>
          <li>Providing continuity during your workflow</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">Cookie Consent</h3>
        <p className="mb-6">
          <strong>No Consent Required:</strong> Under GDPR regulations, we do not need your consent for these cookies as they are strictly necessary for the operation of our service. Without these cookies, our Care Plan Manager cannot function properly.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">What We Don't Use</h3>
        <p className="mb-4">To protect your privacy, we do <strong>NOT</strong> use:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Analytics cookies (like Google Analytics)</li>
          <li>Advertising or marketing cookies</li>
          <li>Social media tracking cookies</li>
          <li>Third-party tracking technologies</li>
          <li>Behavioral profiling cookies</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">Managing Cookies</h3>
        <p className="mb-6">
          Since our cookies are essential for the service to function, disabling them will prevent you from using the Care Plan Manager effectively. However, you can manage cookies through your browser settings:
        </p>

        <h4 className="text-xl font-semibold mt-6 mb-3">To Clear Existing Cookies:</h4>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Use your browser's "Clear browsing data" option</li>
          <li>Select "Cookies and other site data"</li>
          <li>Log out from our application (recommended)</li>
        </ul>

        <h4 className="text-xl font-semibold mt-6 mb-3">Browser Cookie Settings:</h4>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
          <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
          <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">Cookie Security</h3>
        <p className="mb-4">We implement industry-standard security measures for our cookies:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li><strong>Secure transmission:</strong> Cookies are transmitted over encrypted connections</li>
          <li><strong>HttpOnly protection:</strong> Prevents client-side access to sensitive cookies</li>
          <li><strong>Short duration:</strong> Session cookies expire after 1 hour of inactivity</li>
          <li><strong>Automatic cleanup:</strong> Cookies are cleared when you log out</li>
        </ul>

        <h3 className="text-2xl font-bold mt-8 mb-4">Updates to This Policy</h3>
        <p className="mb-6">
          We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new policy on this page.
        </p>

        <h3 className="text-2xl font-bold mt-8 mb-4">Contact Us</h3>
        <p className="mb-2">If you have questions about our use of cookies or this Cookie Policy, please contact us:</p>
        <p className="mb-2"><strong>Data Protection Enquiries</strong></p>
        <p className="mb-2"><strong>Allwayswithyou Limited</strong></p>
        <p className="mb-2">Sonas, Quin, Co Clare, V95Y031, Ireland</p>
        <p className="mb-2">Company Registration No: 622071</p>
        <p className="mb-2">VAT No: IE3530481GH</p>
        <p className="mb-6">Email: <a href="mailto:support@allwayswithyou.com" className="text-blue-600 hover:underline">support@allwayswithyou.com</a></p>
        <p className="mb-6 italic">Please include "Cookie Policy" in your subject line</p>
      </>
    )
  },
  'company-info': {
    title: 'Company Information',
    lastUpdated: 'September 5, 2025',
    content: (
      <>
        <div className="mb-8">
          <img src={logoHeader} alt="Allwayswithyou Logo" className="h-16 w-auto mb-6" />
        </div>

        <h3 className="text-2xl font-bold mt-8 mb-4">Allwayswithyou Limited</h3>
        <p className="mb-8 text-lg text-gray-600">Professional healthcare services and care planning solutions</p>

        <h3 className="text-2xl font-bold mt-8 mb-4">Company Registration</h3>
        
        <div className="mb-6">
          <p className="font-semibold mb-2">Company Name</p>
          <p className="mb-4">Allwayswithyou Limited</p>
        </div>

        <div className="mb-6">
          <p className="font-semibold mb-2">Jurisdiction</p>
          <p className="mb-4">Registered in Ireland</p>
        </div>

        <div className="mb-6">
          <p className="font-semibold mb-2">Company Registration No.</p>
          <p className="mb-4">622071</p>
        </div>

        <div className="mb-6">
          <p className="font-semibold mb-2">VAT Number</p>
          <p className="mb-4">IE3530481GH</p>
        </div>

        <h3 className="text-2xl font-bold mt-8 mb-4">Registered Office</h3>
        <p className="font-semibold mb-2">Address</p>
        <p className="mb-1">Sonas</p>
        <p className="mb-1">Quin, Co. Clare</p>
        <p className="mb-6">V95 Y031</p>
        <p className="mb-8">Ireland</p>

        <h3 className="text-2xl font-bold mt-8 mb-4">Contact Information</h3>
        <p className="font-semibold mb-2">Email</p>
        <p className="mb-8"><a href="mailto:support@allwayswithyou.com" className="text-blue-600 hover:underline">support@allwayswithyou.com</a></p>

        <h3 className="text-2xl font-bold mt-8 mb-4">About Our Services</h3>
        <p className="mb-4">
          Allwayswithyou Limited is a leading provider of professional healthcare services and care planning solutions in Ireland. We specialize in:
        </p>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>Comprehensive care plan management</li>
          <li>Professional healthcare assessments</li>
          <li>Digital healthcare solutions</li>
          <li>Client-centered care coordination</li>
        </ul>

        <p className="mb-2 font-semibold">Need Assistance?</p>
        <p className="mb-6">
          For any questions about our services or company information, please contact us at{' '}
          <a href="mailto:support@allwayswithyou.com" className="text-blue-600 hover:underline">support@allwayswithyou.com</a>
        </p>
      </>
    )
  }
};

export default function LegalPage() {
  const [, params] = useRoute("/:page");
  const pageKey = params?.page || 'privacy-policy';
  
  // Scroll to top when page changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageKey]);
  
  const pageData = legalContent[pageKey];

  if (!pageData) {
    return <div>Page not found</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 sticky top-0 bg-white z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link href="/" data-testid="link-home">
            <a className="flex items-center gap-2">
              <img src={logoHeader} alt="Allwayswithyou logo" className="h-8 w-auto" />
            </a>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="/#value" className="hover:underline">Why</a>
            <a href="/#centre" className="hover:underline">Care Plan</a>
            <a href="/#pilot-programme" className="hover:underline">Pilot Programme</a>
            <Link href="/blog" data-testid="link-blog">
              <a className="hover:underline">Blog/News</a>
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <a href="https://cpm.allwayswithyou.com" target="_blank" rel="noopener noreferrer" data-testid="button-cpm-login" className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">CPM Login</a>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold mb-2" data-testid="text-page-title">{pageData.title}</h1>
        <p className="text-sm text-gray-600 mb-8">Last Updated: {pageData.lastUpdated}</p>
        
        <div className="prose prose-lg max-w-none">
          {pageData.content}
        </div>

        <div className="mt-12 pt-6 border-t border-gray-200">
          <Link href="/" data-testid="link-back-home" className="text-blue-600 hover:underline">
            ← Go Back
          </Link>
        </div>
      </main>

      {/* Footer */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-extrabold">Ready to see it in your workflow?</h2>
          <p className="mt-3 text-slate-300 max-w-2xl mx-auto">Start with a pilot site or roll out across branches. We'll map your assessments and approvals to Allwayswithyou.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="mailto:info@allwayswithyou.com" data-testid="button-schedule-demo" className="px-5 py-3 rounded-xl bg-white text-slate-900 font-semibold hover:bg-slate-100">Schedule Demo</a>
            <a href="mailto:info@allwayswithyou.com" data-testid="button-get-quote" className="px-5 py-3 rounded-xl bg-white/10 text-white font-semibold border border-white/20 hover:bg-white/20">Get a Tailored Quote</a>
          </div>
          <div className="mt-12 text-left bg-white/5 rounded-2xl p-6 md:p-8 mx-auto max-w-4xl">
            <h3 className="font-semibold mb-2">About Allwayswithyou</h3>
            <p className="text-slate-200">
              Allwayswithyou is a digital platform that transforms how healthcare providers create, manage, and communicate care plans — across homecare, cancer care, disability, and community health services. By automating care plan generation from standard assessments, it delivers consistent, compliant documentation in minutes rather than hours. Role-based insights and collaboration tools connect assessors, clinicians, and families in real time, ensuring clarity and accountability at every stage of care. Designed as a secure, configurable SaaS platform, Allwayswithyou helps organisations raise quality, reduce administrative burden, and strengthen trust between patients, families, and multidisciplinary care teams.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-4 mt-10">
            <img src={logoFooter} alt="Allwayswithyou full logo" className="w-40 h-auto" />
            <nav className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-slate-400">
              <Link href="/privacy-policy" className="hover:text-white transition-colors" data-testid="link-privacy-policy">Privacy Policy</Link>
              <Link href="/terms-conditions" className="hover:text-white transition-colors" data-testid="link-terms-conditions">Terms &amp; Conditions</Link>
              <Link href="/cookie-policy" className="hover:text-white transition-colors" data-testid="link-cookie-policy">Cookie Policy</Link>
              <Link href="/company-info" className="hover:text-white transition-colors" data-testid="link-company-info">Company Information</Link>
            </nav>
            <p className="text-slate-400 text-sm">© 2025 Allwayswithyou. All rights reserved.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
